/**
 * NotificationInfo Remapper
 *
 * Bridges Firestore wire JSON ↔ `NotificationInfoDto` ↔ `NotificationInfoEntity`,
 * following the `fromJson(): Dto` + `fromDto(): Entity` convention codified by
 * `chatMessageRemapper.ts` and `data-layer.md`.
 * Flutter source: lib/data/models/notification_info.dart
 */

import type { NotificationInfoDto } from '@/data/dtos/info/notificationInfoDto';
import type { JsonObject } from '@/data/types/json';
import type { NotificationInfoEntity } from '@/domain/entities/notification/notificationInfoEntity';
import { BroadcastingInfoRemapper } from '@/domain/remappers/info/broadcastingInfoRemapper';
import { CampaignInfoRemapper } from '@/domain/remappers/info/campaignInfoRemapper';
import { CommentInfoRemapper } from '@/domain/remappers/info/commentInfoRemapper';
import { GiftInfoRemapper } from '@/domain/remappers/info/giftInfoRemapper';
import { IdentityVerificationInfoRemapper } from '@/domain/remappers/info/identityVerificationInfoRemapper';
import { MediaInfoRemapper } from '@/domain/remappers/info/mediaInfoRemapper';
import { PremiumPackageInfoRemapper } from '@/domain/remappers/info/premiumPackageInfoRemapper';
import { RoadmapCommentInfoRemapper } from '@/domain/remappers/info/roadmapCommentInfoRemapper';
import { RoadmapItemInfoRemapper } from '@/domain/remappers/info/roadmapItemInfoRemapper';
import { SubscriptionInfoRemapper } from '@/domain/remappers/info/subscriptionInfoRemapper';
import { UsersInfoRemapper } from '@/domain/remappers/info/usersInfoRemapper';

export class NotificationInfoRemapper {
  /**
   * Parse raw Firestore JSON into a `NotificationInfoDto`. Nested shapes are
   * routed through their existing sub-remappers' `fromJson` (each of which
   * returns its own DTO — no field-copy here) so the wire shape can never drift
   * from the sub-DTOs. No `as`-to-entity casts.
   */
  static fromJson(data: JsonObject): NotificationInfoDto {
    const usersInfo = (data['usersInfo'] as JsonObject | undefined) || {};
    const mediaInfo = data['mediaInfo'] as JsonObject | undefined;
    const giftInfo = data['giftInfo'] as JsonObject | undefined;
    const subscriptionInfo = data['subscriptionInfo'] as JsonObject | undefined;
    const commentInfo = data['commentInfo'] as JsonObject | undefined;
    const broadcastingInfo = data['broadcastingInfo'] as JsonObject | undefined;
    const identityVerificationInfo = data['identityVerificationInfo'] as
      | JsonObject
      | undefined;
    const premiumPackageInfo = data['premiumPackageInfo'] as JsonObject | undefined;
    const campaignInfo = data['campaignInfo'] as JsonObject | undefined;
    const roadmapItemInfo = data['roadmapItemInfo'] as JsonObject | undefined;
    const roadmapCommentInfo = data['roadmapCommentInfo'] as JsonObject | undefined;
    return {
      usersInfo: UsersInfoRemapper.fromJson(usersInfo),
      mediaInfo: mediaInfo ? MediaInfoRemapper.fromJson(mediaInfo) : undefined,
      giftInfo: giftInfo ? GiftInfoRemapper.fromJson(giftInfo) : undefined,
      subscriptionInfo: subscriptionInfo
        ? SubscriptionInfoRemapper.fromJson(subscriptionInfo)
        : undefined,
      commentInfo: commentInfo
        ? CommentInfoRemapper.fromJson(commentInfo)
        : undefined,
      broadcastingInfo: broadcastingInfo
        ? BroadcastingInfoRemapper.fromJson(broadcastingInfo)
        : undefined,
      identityVerificationInfo: identityVerificationInfo
        ? IdentityVerificationInfoRemapper.fromJson(identityVerificationInfo)
        : undefined,
      premiumPackageInfo: premiumPackageInfo
        ? PremiumPackageInfoRemapper.fromJson(premiumPackageInfo)
        : undefined,
      campaignInfo: campaignInfo
        ? CampaignInfoRemapper.fromJson(campaignInfo)
        : undefined,
      roadmapItemInfo: roadmapItemInfo
        ? RoadmapItemInfoRemapper.fromJson(roadmapItemInfo)
        : undefined,
      roadmapCommentInfo: roadmapCommentInfo
        ? RoadmapCommentInfoRemapper.fromJson(roadmapCommentInfo)
        : undefined,
      notificationType: (data['notificationType'] as number | undefined) ?? 0,
      read: (data['read'] as boolean | undefined) ?? false,
      actioned: data['actioned'] as boolean | undefined,
      actionMaker: data['actionMaker'] as string | undefined,
      isUndo: (data['isUndo'] as boolean | undefined) ?? false,
      timestamp: (data['timestamp'] as number | undefined) ?? 0,
    };
  }

  /**
   * Build a `NotificationInfoEntity` from a `NotificationInfoDto`.
   *
   * `usersInfo`, `mediaInfo`, and `commentInfo` differ in
   * optionality/null-vs-undefined between layers, so they route through the
   * sub-remapper's `fromDto` bridge (`MediaInfoEntity` requires
   * `aspectRatio`/`allowUnlock`; `CommentInfoEntity` uses `undefined`, not
   * `null`). The remaining nested shapes (gift/subscription/broadcasting/
   * campaign/identityVerification/premiumPackage/roadmapItem/roadmapComment) are
   * structurally identical DTO↔entity today, so they assign through directly —
   * faithfully reproducing the previous `fromJson` body and keeping this fix
   * scoped (no `fromDto` is added to the sub-remappers that lack one).
   */
  static fromDto(dto: NotificationInfoDto): NotificationInfoEntity {
    return {
      usersInfo: UsersInfoRemapper.fromDto(dto.usersInfo),
      mediaInfo: dto.mediaInfo
        ? MediaInfoRemapper.fromDto(dto.mediaInfo)
        : undefined,
      giftInfo: dto.giftInfo,
      subscriptionInfo: dto.subscriptionInfo,
      commentInfo: dto.commentInfo
        ? CommentInfoRemapper.fromDto(dto.commentInfo)
        : undefined,
      broadcastingInfo: dto.broadcastingInfo,
      identityVerificationInfo: dto.identityVerificationInfo,
      premiumPackageInfo: dto.premiumPackageInfo,
      campaignInfo: dto.campaignInfo,
      roadmapItemInfo: dto.roadmapItemInfo,
      roadmapCommentInfo: dto.roadmapCommentInfo,
      notificationType: dto.notificationType,
      read: dto.read,
      actioned: dto.actioned,
      actionMaker: dto.actionMaker,
      isUndo: dto.isUndo,
      timestamp: dto.timestamp,
    };
  }

  static toJson(dto: NotificationInfoDto): JsonObject {
    const map: JsonObject = {
      usersInfo: UsersInfoRemapper.toJson(dto.usersInfo),
      notificationType: dto.notificationType,
      read: dto.read,
      isUndo: dto.isUndo,
      timestamp: dto.timestamp,
    };
    if (dto.mediaInfo) map.mediaInfo = MediaInfoRemapper.toJson(dto.mediaInfo);
    if (dto.commentInfo) map.commentInfo = CommentInfoRemapper.toJson(dto.commentInfo);
    // Additive extension: the live-notification write is the only caller that
    // sets `broadcastingInfo` (community_screen.dart:628,681). Comment/roadmap/
    // mention writes pass `broadcastingInfo === undefined`, so their emitted JSON
    // is byte-unchanged. Parity: `_$NotificationInfoToJson` emits the nested map
    // via `broadcastingInfo?.toJson()` (notification_info.g.dart:67), all 4 keys.
    if (dto.broadcastingInfo)
      map.broadcastingInfo = BroadcastingInfoRemapper.toJson(dto.broadcastingInfo);
    if (dto.roadmapItemInfo)
      map.roadmapItemInfo = RoadmapItemInfoRemapper.toJson(dto.roadmapItemInfo);
    if (dto.roadmapCommentInfo)
      map.roadmapCommentInfo = RoadmapCommentInfoRemapper.toJson(dto.roadmapCommentInfo);
    if (dto.actioned !== undefined) map.actioned = dto.actioned;
    if (dto.actionMaker !== undefined) map.actionMaker = dto.actionMaker;
    return map;
  }

  /**
   * Build a `NotificationInfoDto` from a `NotificationInfoEntity` — the faithful
   * reverse of `fromDto`. Nested shapes that `fromDto` bridges via a sub-remapper
   * (`usersInfo`, `mediaInfo`, `commentInfo`) are reversed through the matching
   * entity→dto method; `broadcastingInfo` is bridged via
   * `BroadcastingInfoRemapper.toDtoFromEntity` when defined. Scalars are carried
   * (`read`/`isUndo` re-apply the model defaults `false` when unset, matching
   * `fromDto`'s DTO defaults).
   *
   * The write path is currently exercised only by the live notification
   * (community_screen.dart:625-635,678-689), which sets just `usersInfo` +
   * `broadcastingInfo`; the remaining nested info fields
   * (`giftInfo`/`subscriptionInfo`/`identityVerificationInfo`/`premiumPackageInfo`/
   * `campaignInfo`/`roadmapItemInfo`/`roadmapCommentInfo`) are structurally
   * identical DTO↔entity and carry through as-is (all `undefined` for a live
   * notification). A future writer of a richer notification type extends the
   * nested-field conversions then.
   */
  static toDtoFromEntity(entity: NotificationInfoEntity): NotificationInfoDto {
    return {
      usersInfo: UsersInfoRemapper.toDtoFromEntity(entity.usersInfo),
      mediaInfo: entity.mediaInfo
        ? MediaInfoRemapper.toDto(entity.mediaInfo)
        : undefined,
      giftInfo: entity.giftInfo,
      subscriptionInfo: entity.subscriptionInfo,
      commentInfo: entity.commentInfo
        ? CommentInfoRemapper.toDto(entity.commentInfo)
        : undefined,
      broadcastingInfo: entity.broadcastingInfo
        ? BroadcastingInfoRemapper.toDtoFromEntity(entity.broadcastingInfo)
        : undefined,
      identityVerificationInfo: entity.identityVerificationInfo,
      premiumPackageInfo: entity.premiumPackageInfo,
      campaignInfo: entity.campaignInfo,
      roadmapItemInfo: entity.roadmapItemInfo,
      roadmapCommentInfo: entity.roadmapCommentInfo,
      notificationType: entity.notificationType,
      read: entity.read ?? false,
      isUndo: entity.isUndo ?? false,
      timestamp: entity.timestamp,
      actioned: entity.actioned,
      actionMaker: entity.actionMaker,
    };
  }
}
